/**
 * @format
 */

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { ButtonToolbar, Col, Form, Row } from 'reactstrap';
import {
	CancelButton,
	DateInputWithLabel,
	DropdownSelectWithLabel,
	EmailInputWithLabel,
	PhoneInputWithLabel,
	SaveButton,
	TextInputWithLabel,
	TextAreaWithLabel,
	ZipInputWithLabel,
	AddressSelectWithLabel,
	PatientValidateButton,
	DropdownSelectWithLabelAndDynamicValue
} from '../common/standardComponents';
import { validateDateOfBirth } from '../../lib/validation';
import PatientDetailsFromPatientAccessCurator from './patientDetailsFromPatientAccessCurator';
import { pacPhoneTypeEnum } from '../../constants/patientAccessCuratorEnum'
import './patientEditForm.css';

const emptyAddressOption = {
	label: "Select...", value: {}
};

export default function PatientEditForm(props) {
	const [isManualAddressEntry, setIsManualAddressEntry] = useState(!props.patientConfig.showAddressSearch);
	
	const {
		patientConfig,
		genderOptions,
		shouldValidate,
		patientDetails,
		onPatientChange,
		phoneForceInvalid,
		zipCodeForceInvalid,
		patientPoolForceInvalid,
		stateList,
		handleAddressChange,
		loadAddressOptions,
		selectedAddressOption,
		isAddressLoading,
		isPacValidated,
		handleValidate,
		pacDemographics,
		isPacValidating,
		handleUpdatePatientInfoFromPac,
		patientPools,
		disablePatientPoolSelection,
	} = props;

	const isPatientAccessCuratorEnabled = patientConfig.enablePatientAccessCurator;
	const isPacValidationForCreateEnabled = isPatientAccessCuratorEnabled && patientConfig.enablePatientAccessCuratorValidationForCreate;
	

	const [isFirstNamePacInValid, setIsFirstNamePacInValid] = useState(false);
	const [isMiddleNamePacInValid, setIsMiddleNamePacInValid] = useState(false);
	const [isLastNamePacInValid, setIsLastNamePacInValid] = useState(false);
	const [isDateOfBirthPacInValid, setIsDateOfBirthPacInValid] = useState(false);
	const [isGenderPacInValid, setIsGenderPacInValid] = useState(false);
	const [isMobilePhonePacInValid, setIsMobilePhonePacInValid] = useState(false);
	const [isHomePhonePacInValid, setIsHomePhonePacInValid] = useState(false);
	const [isAlternatePhonePacInValid, setIsAlternatePhonePacInValid] = useState(false);
	const [isAddressLine1PacInValid, setIsAddressLine1PacInValid] = useState(false);
	const [isAddressLine2PacInValid, setIsAddressLine2PacInValid] = useState(false);
	const [isCityNamePacInValid, setIsCityNamePacInValid] = useState(false);
	const [isStateIdPacInValid, setIsStateIdPacInValid] = useState(false);
	const [isZipCodePacInValid, setIsZipCodePacInValid] = useState(false);
	const [isEmailPacInValid, setIsEmailPacInValid] = useState(false);

	const [formattedPacResponse, setFormattedPacResponse] = useState({});
	const [currentPatientDetails, setCurrentPatientDetails] = useState({});

	let isMinor = moment().diff(patientDetails.dateOfBirth, 'years') < patientConfig.guardian.adultAge ? true : false;

	let genderLabel =
		patientConfig.gender && patientConfig.gender.fieldLabel ? patientConfig.gender.fieldLabel : 'Gender';
	let memberIdLabel =
		patientConfig.memberId && patientConfig.memberId.fieldLabel ? patientConfig.memberId.fieldLabel : 'Member Id';
	let firstNameLabel =
		patientConfig.firstName && patientConfig.firstName.fieldLabel ? patientConfig.firstName.fieldLabel : 'First Name';
	let middleNameLabel =
		patientConfig.middleName && patientConfig.middleName.fieldLabel
			? patientConfig.middleName.fieldLabel
			: 'Middle Name';
	let lastNameLabel =
		patientConfig.lastName && patientConfig.lastName.fieldLabel ? patientConfig.lastName.fieldLabel : 'Last Name';
	let groupNumberLabel =
		patientConfig.groupNumber && patientConfig.groupNumber.fieldLabel
			? patientConfig.groupNumber.fieldLabel
			: 'Group Number';
	let addressLine1Label =
		patientConfig.addressLine1 && patientConfig.addressLine1.fieldLabel
			? patientConfig.addressLine1.fieldLabel
			: 'Address (Line 1)';
	let addressLine2Label =
		patientConfig.addressLine2 && patientConfig.addressLine2.fieldLabel
			? patientConfig.addressLine2.fieldLabel
			: 'Address (Line 2)';
	let cityLabel = patientConfig.city && patientConfig.city.fieldLabel ? patientConfig.city.fieldLabel : 'City';
	let stateLabel = patientConfig.state && patientConfig.state.fieldLabel ? patientConfig.state.fieldLabel : 'State';
	let zipCodeLabel =
		patientConfig.zipCode && patientConfig.zipCode.fieldLabel ? patientConfig.zipCode.fieldLabel : 'Zip Code';
	let dateOfBirthLabel =
		patientConfig.dateOfBirth && patientConfig.dateOfBirth.fieldLabel
			? patientConfig.dateOfBirth.fieldLabel
			: 'Date of Birth';
	let homePhoneLabel =
		patientConfig.homePhone && patientConfig.homePhone.fieldLabel ? patientConfig.homePhone.fieldLabel : 'Home Phone';
	let mobilePhoneLabel =
		patientConfig.mobilePhone && patientConfig.mobilePhone.fieldLabel
			? patientConfig.mobilePhone.fieldLabel
			: 'Mobile Phone';
	let alternatePhoneLabel =
		patientConfig.alternatePhone && patientConfig.alternatePhone.fieldLabel
			? patientConfig.alternatePhone.fieldLabel
			: 'Alternate Phone';
	let emailLabel =
		patientConfig.email && patientConfig.email.fieldLabel ? patientConfig.email.fieldLabel : 'Email Address';
	let parentOrGuardianNameLabel =
		patientConfig.parentOrGuardianName && patientConfig.parentOrGuardianName.fieldLabel
			? patientConfig.parentOrGuardianName.fieldLabel
			: 'Guardian Name';
	let patientNotesLabel =
		patientConfig.notes && patientConfig.notes.fieldLabel ? patientConfig.notes.fieldLabel : 'Patient Notes';

	let gendersList = [{ id: '', name: 'Select', key: -1 }];

	useEffect(() => {
		if (Object.keys(pacDemographics).length !== 0) {
			formatPacResponse(pacDemographics);
		}
		else {
			setFormattedPacResponse(pacDemographics);
		}
	}, [pacDemographics])

	useEffect(() => {
		if (Object.keys(formattedPacResponse).length !== 0) {
			setIsFirstNamePacInValid(patientDetails.firstName?.toLowerCase() !== formattedPacResponse.firstName.toLowerCase());
			setIsMiddleNamePacInValid(patientDetails.middleName?.toLowerCase() !== formattedPacResponse.middleName.toLowerCase());
			setIsLastNamePacInValid(patientDetails.lastName?.toLowerCase() !== formattedPacResponse.lastName.toLowerCase());
			setIsDateOfBirthPacInValid(patientDetails.dateOfBirth !== formattedPacResponse.dateOfBirth);
			setIsAddressLine1PacInValid(patientDetails.addressLine1?.toLowerCase() !== formattedPacResponse.addressLine1.toLowerCase());
			setIsAddressLine2PacInValid(patientDetails.addressLine2?.toLowerCase() !== formattedPacResponse.addressLine2.toLowerCase());
			setIsCityNamePacInValid(patientDetails.cityName?.toLowerCase() !== formattedPacResponse.cityName.toLowerCase());
			setIsStateIdPacInValid(patientDetails.stateId.toString() !== formattedPacResponse.stateId);
			setIsZipCodePacInValid(patientDetails.zipCode !== formattedPacResponse.zipCode);
			setIsEmailPacInValid((patientDetails.email ? patientDetails.email.toLowerCase() : '') !== formattedPacResponse.email.toLowerCase());
			setIsGenderPacInValid(patientDetails.gender !== formattedPacResponse.gender);
			setIsMobilePhonePacInValid(formattedPacResponse.isMobilePhone && patientDetails.mobilePhone !== formattedPacResponse.phone);
			setIsHomePhonePacInValid(formattedPacResponse.isHomePhone && patientDetails.homePhone !== formattedPacResponse.phone);
			setIsAlternatePhonePacInValid(formattedPacResponse.isAlternatePhone && patientDetails.alternatePhone !== formattedPacResponse.phone);
		}
		else {
			setIsFirstNamePacInValid(false);
			setIsMiddleNamePacInValid(false);
			setIsLastNamePacInValid(false);
			setIsDateOfBirthPacInValid(false);
			setIsAddressLine1PacInValid(false);
			setIsAddressLine2PacInValid(false);
			setIsCityNamePacInValid(false);
			setIsStateIdPacInValid(false);
			setIsZipCodePacInValid(false);
			setIsEmailPacInValid(false);
			setIsGenderPacInValid(false);
			setIsMobilePhonePacInValid(false);
			setIsHomePhonePacInValid(false);
			setIsAlternatePhonePacInValid(false);
		}
	}, [formattedPacResponse, patientDetails])

	const formatPacResponse = (data) => {
		const pacState = data.addresses[0]?.state != null ? stateList.find((x) => x.name === data.addresses[0].state) : null;
		setFormattedPacResponse({
			...formattedPacResponse,
			firstName: ((data.firstName) ? data.firstName : ''),
			middleName: ((data.middleName) ? data.middleName : ''),
			lastName: ((data.lastName) ? data.lastName : ''),
			dateOfBirth: (data.dateOfBirth ? moment(data.dateOfBirth).format('MM/DD/YYYY') : ''),
			addressLine1: ((data.addresses[0]?.addressLine1) ? data.addresses[0].addressLine1 : ''),
			addressLine2: ((data.addresses[0]?.addressLine2) ? data.addresses[0].addressLine2 : ''),
			cityName: ((data.addresses[0]?.city) ? data.addresses[0].city : ''),
			stateId: (pacState.id.toString()),
			state: ((data.addresses[0]?.state) ? data.addresses[0].state : ''),
			zipCode: ((data.addresses[0]?.zip) ? data.addresses[0].zip : ''),
			email: (data.emailAddress ? data.emailAddress : ''),
			gender: ((data.gender) ? data.gender : ''),
			phone: data.phoneNumber,
			isMobilePhone: ((data.phoneType && data.phoneType === pacPhoneTypeEnum.Mobile) ? true : false),
			isHomePhone: ((data.phoneType && data.phoneType === pacPhoneTypeEnum.Standard) ? true : false),
			isAlternatePhone: (data.phoneType && data.phoneType !== pacPhoneTypeEnum.Standard && data.phoneType !== pacPhoneTypeEnum.Mobile)
		});
	}

	genderOptions.forEach((s, i) => {
		gendersList.push({ id: s.id, name: s.name, key: i });
	});

	const handleIsManualAddressEntryChange = (e) => {
		handleAddressChange(emptyAddressOption);
		setIsManualAddressEntry(e);
	}

	const handleKeyDown = (e) => {
		if (e.key === 'Delete') {
			e.preventDefault();
			handleAddressChange(emptyAddressOption);
		}
	}

	const handleUpdateInfo = (e) => {
		e.preventDefault();
		setCurrentPatientDetails({
			...patientDetails,
			firstName: formattedPacResponse.firstName ? formattedPacResponse.firstName : patientDetails.firstName,
			middleName: formattedPacResponse.middleName ? formattedPacResponse.middleName : patientDetails.middleName,
			lastName: formattedPacResponse.lastName ? formattedPacResponse.lastName : patientDetails.lastName,
			dateOfBirth: formattedPacResponse.dateOfBirth ? formattedPacResponse.dateOfBirth : patientDetails.dateOfBirth,
			addressLine1: formattedPacResponse.addressLine1 ? formattedPacResponse.addressLine1 : patientDetails.addressLine1,
			addressLine2: formattedPacResponse.addressLine2 ? formattedPacResponse.addressLine2 : patientDetails.addressLine2,
			cityName: formattedPacResponse.cityName ? formattedPacResponse.cityName : patientDetails.cityName,
			stateId: formattedPacResponse.stateId ? formattedPacResponse.stateId : patientDetails.stateId,
			zipCode: formattedPacResponse.zipCode ? formattedPacResponse.zipCode : patientDetails.zipCode,
			email: formattedPacResponse.email ? formattedPacResponse.email : patientDetails.email,
			gender: formattedPacResponse.gender ? formattedPacResponse.gender : patientDetails.gender,
			mobilePhone: (formattedPacResponse.phone && formattedPacResponse.isMobilePhone) ? formattedPacResponse.phone : patientDetails.mobilePhone,
			homePhone: (formattedPacResponse.phone && formattedPacResponse.isHomePhone) ? formattedPacResponse.phone : patientDetails.homePhone,
			alternatePhone: (formattedPacResponse.phone && formattedPacResponse.isAlternatePhone) ? formattedPacResponse.phone : patientDetails.alternatePhone,
		});
	}
	useEffect(() => {
		if (Object.keys(currentPatientDetails).length !== 0) {
			handleUpdatePatientInfoFromPac(currentPatientDetails);
		}
	}, [currentPatientDetails])

	return (
		<Form>
			<Row className="form-area">
				<Col className="patient-demographic-area">
					<div>
						{((patientConfig.memberId && patientConfig.memberId.isVisible) ||
							(patientConfig.groupNumber && patientConfig.groupNumber.isVisible) ||
							(patientConfig.enablePatientSearchWithPool && (patientPools.length > 0))) && (
							<Row>
								{patientConfig.enablePatientSearchWithPool && (patientPools.length > 0) && (
									<Col lg="4">
										<DropdownSelectWithLabelAndDynamicValue
											id="patientPoolUniqueToken"
											label="Patient Pool"
											value={patientDetails.patientPoolUniqueToken || patientDetails.patientPool?.uniqueToken || ''}
											isRequired={true}
											disabled={patientPools.length === 1 || disablePatientPoolSelection}
											shouldValidate={shouldValidate}
											onChange={onPatientChange}
											optionList={patientPools}
											optionValue="uniqueToken"
											optionName="displayName"
											forceInvalid={patientPoolForceInvalid}
										/>
									</Col>
								)}
								{patientConfig.memberId && patientConfig.memberId.isVisible && (
									<Col lg="4">
										<TextInputWithLabel
											id="memberId"
											label={memberIdLabel}
											value={patientDetails.memberId}
											maxLength={patientConfig.memberId.maxLength}
											isRequired={patientConfig.memberId.isRequired}
											disabled={!patientConfig.memberId.isEditable}
											shouldValidate={shouldValidate}
											onChange={onPatientChange}
										/>
									</Col>
								)}
								{patientConfig.groupNumber && patientConfig.groupNumber.isVisible && (
									<Col lg="4">
										<TextInputWithLabel
											id="groupNumber"
											label={groupNumberLabel}
											value={patientDetails.groupNumber}
											maxLength={patientConfig.groupNumber.maxLength}
											isRequired={patientConfig.groupNumber.isRequired}
											disabled={!patientConfig.groupNumber.isEditable}
											shouldValidate={shouldValidate}
											onChange={onPatientChange}
										/>
									</Col>
								)}
							</Row>
						)}
						<Row>
							{patientConfig.firstName && patientConfig.firstName.isVisible && (
								<Col lg="4">
									<TextInputWithLabel
										id="firstName"
										label={firstNameLabel}
										value={patientDetails.firstName}
										maxLength={50}
										isRequired={patientConfig.firstName.isRequired || isPacValidationForCreateEnabled}
										disabled={!patientConfig.firstName.isEditable}
										shouldValidate={shouldValidate}
										onChange={onPatientChange}
										isPacInValid={isFirstNamePacInValid}
									/>
								</Col>
							)}
							{patientConfig.middleName && patientConfig.middleName.isVisible && (
								<Col lg="4">
									<TextInputWithLabel
										id="middleName"
										label={middleNameLabel}
										value={patientDetails.middleName}
										maxLength={50}
										isRequired={patientConfig.middleName.isRequired}
										disabled={!patientConfig.middleName.isEditable}
										shouldValidate={shouldValidate}
										onChange={onPatientChange}
										isPacInValid={isMiddleNamePacInValid}
									/>
								</Col>
							)}
							{patientConfig.lastName && patientConfig.lastName.isVisible && (
								<Col lg="4">
									<TextInputWithLabel
										id="lastName"
										label={lastNameLabel}
										value={patientDetails.lastName}
										maxLength={50}
										isRequired={patientConfig.lastName.isRequired || isPacValidationForCreateEnabled}
										disabled={!patientConfig.lastName.isEditable}
										shouldValidate={shouldValidate}
										onChange={onPatientChange}
										isPacInValid={isLastNamePacInValid}
									/>
								</Col>
							)}
						</Row>
						<Row>
							{patientConfig.notes && patientConfig.notes.isVisible && (
								<Col lg="12">
									<TextAreaWithLabel
										id="notes"
										label={patientNotesLabel}
										value={patientDetails.notes}
										isRequired={patientConfig.notes.isRequired}
										disabled={!patientConfig.notes.isEditable}
										shouldValidate={shouldValidate}
										onChange={onPatientChange}
									/>
								</Col>
							)}
						</Row>
						<Row>
							{patientConfig.dateOfBirth && patientConfig.dateOfBirth.isVisible && (
								<Col lg="4">
									<DateInputWithLabel
										id="dateOfBirth"
										label={dateOfBirthLabel}
										value={patientDetails.dateOfBirth}
										isRequired={patientConfig.dateOfBirth.isRequired || isPacValidationForCreateEnabled}
										validator={validateDateOfBirth}
										disabled={!patientConfig.dateOfBirth.isEditable}
										shouldValidate={shouldValidate}
										onChange={onPatientChange}
										isPacInValid={isDateOfBirthPacInValid}
									/>
								</Col>
							)}
							<Col lg="4">
								{patientConfig.parentOrGuardianName &&
									patientConfig.parentOrGuardianName.isVisible &&
									isMinor && ( //{showGuardian &&
										<TextInputWithLabel
											id="guardianName"
											label={parentOrGuardianNameLabel}
											value={patientDetails.guardianName}
											maxLength={50}
											isRequired={patientConfig.parentOrGuardianName.isRequired}
											disabled={!patientConfig.parentOrGuardianName.isEditable}
											shouldValidate={shouldValidate}
											onChange={onPatientChange}
										/>
									)}
							</Col>
							<Col lg="4">
								<div>&nbsp;</div>
							</Col>
						</Row>
						<Row>
							{patientConfig.gender && patientConfig.gender.isVisible && (
								<Col lg="4">
									<DropdownSelectWithLabel
										id="gender"
										label={genderLabel}
										value={patientDetails.gender}
										isRequired={patientConfig.gender.isRequired}
										disabled={!patientConfig.gender.isEditable}
										shouldValidate={shouldValidate}
										onChange={onPatientChange}
										optionList={gendersList}
										isPacInValid={isGenderPacInValid}
									/>
								</Col>
							)}
						</Row>
						<Row>
							{patientConfig.homePhone && patientConfig.homePhone.isVisible && (
								<Col lg="4">
									<PhoneInputWithLabel
										id="homePhone"
										label={homePhoneLabel}
										forcedInvalidMessage="Please provide a valid Home or Mobile Phone"
										subText={patientConfig.homePhone.isRequired ? 'Home or Mobile Phone required' : ''}
										value={patientDetails.homePhone}
										isRequired={patientConfig.homePhone.isRequired}
										disabled={!patientConfig.homePhone.isEditable}
										shouldValidate={shouldValidate}
										forceInvalid={phoneForceInvalid}
										onChange={onPatientChange}
										isPacInValid={isHomePhonePacInValid}
									/>
								</Col>
							)}
							{patientConfig.mobilePhone && patientConfig.mobilePhone.isVisible && (
								<Col lg="4">
									<PhoneInputWithLabel
										id="mobilePhone"
										label={mobilePhoneLabel}
										forcedInvalidMessage=" "
										value={patientDetails.mobilePhone}
										shouldValidate={shouldValidate}
										isRequired={patientConfig.mobilePhone.isRequired}
										forceInvalid={phoneForceInvalid}
										disabled={!patientConfig.mobilePhone.isEditable}
										onChange={onPatientChange}
										isPacInValid={isMobilePhonePacInValid}
									/>
								</Col>
							)}
							{patientConfig.alternatePhone && patientConfig.alternatePhone.isVisible && (
								<Col lg="4">
									<PhoneInputWithLabel
										id="alternatePhone"
										label={alternatePhoneLabel}
										forcedInvalidMessage=" "
										value={patientDetails.alternatePhone}
										shouldValidate={shouldValidate}
										isRequired={patientConfig.alternatePhone.isRequired}
										disabled={!patientConfig.alternatePhone.isEditable}
										onChange={onPatientChange}
										isPacInValid={isAlternatePhonePacInValid}
									/>
								</Col>
							)}
						</Row>
						<Row>
							{patientConfig.showAddressSearch &&
								<>
									<AddressSelectWithLabel
										id="address-select"
										className="address-search-select"
										label={isAddressLoading ? "Loading..." : "Address Search"}
										cacheOptions
										defaultOptions={[emptyAddressOption]}
										loadOptions={loadAddressOptions}
										onChange={handleAddressChange}
										value={selectedAddressOption}
										isDisabled={isManualAddressEntry || isAddressLoading}
										isAddressLoading={isAddressLoading}
										onKeyDown={handleKeyDown}
									/>
									<AddressCheckbox
										isManualEntry={isManualAddressEntry}
										onChange={handleIsManualAddressEntryChange}
										disabled={isAddressLoading}
									/>
								</>
							}
						</Row>
						<Row>
							{patientConfig.addressLine1 && patientConfig.addressLine1.isVisible && (
								<Col lg="4">
									<TextInputWithLabel
										id="addressLine1"
										label={addressLine1Label}
										value={patientDetails.addressLine1}
										isRequired={patientConfig.addressLine1.isRequired || isPacValidationForCreateEnabled}
										disabled={!patientConfig.addressLine1.isEditable || !isManualAddressEntry}
										shouldValidate={shouldValidate}
										onChange={onPatientChange}
										isPacInValid={isAddressLine1PacInValid}
									/>
								</Col>
							)}
							{patientConfig.addressLine2 && patientConfig.addressLine2.isVisible && (
								<Col lg="4">
									<TextInputWithLabel
										id="addressLine2"
										label={addressLine2Label}
										value={patientDetails.addressLine2 ? patientDetails.addressLine2 : ''}
										isRequired={patientConfig.addressLine2.isRequired}
										disabled={!patientConfig.addressLine2.isEditable || !isManualAddressEntry}
										shouldValidate={shouldValidate}
										onChange={onPatientChange}
										isPacInValid={isAddressLine2PacInValid}
									/>
								</Col>
							)}
						</Row>
						<Row>
							{patientConfig.city && patientConfig.city.isVisible && (
								<Col lg="4">
									<TextInputWithLabel
										id="cityName"
										label={cityLabel}
										value={patientDetails.cityName}
										isRequired={patientConfig.city.isRequired || isPacValidationForCreateEnabled}
										disabled={!patientConfig.city.isEditable || !isManualAddressEntry}
										shouldValidate={shouldValidate}
										onChange={onPatientChange}
										isPacInValid={isCityNamePacInValid}
									/>
								</Col>
							)}
							{patientConfig.state && patientConfig.state.isVisible && (
								<Col lg="4">
									<DropdownSelectWithLabel
										optionList={stateList}
										id="stateId"
										label={stateLabel}
										value={patientDetails.stateId}
										isRequired={patientConfig.state.isRequired || isPacValidationForCreateEnabled}
										disabled={!patientConfig.state.isEditable || !isManualAddressEntry}
										shouldValidate={shouldValidate}
										onChange={onPatientChange}
										isPacInValid={isStateIdPacInValid}
									/>
								</Col>
							)}
							{patientConfig.zipCode && patientConfig.zipCode.isVisible && (
								<Col lg="4">
									<ZipInputWithLabel
										id="zipCode"
										label={zipCodeLabel}
										value={patientDetails.zipCode}
										isRequired={patientConfig.zipCode.isRequired || isPacValidationForCreateEnabled}
										forceInvalid={zipCodeForceInvalid}
										disabled={!patientConfig.zipCode.isEditable || !isManualAddressEntry}
										shouldValidate={shouldValidate}
										onChange={onPatientChange}
										isPacInValid={isZipCodePacInValid}
									/>
								</Col>
							)}
						</Row>
						<Row>
							{patientConfig.email && patientConfig.email.isVisible && (
								<Col lg="4">
									<EmailInputWithLabel
										id="email"
										label={emailLabel}
										value={patientDetails.email}
										isRequired={patientConfig.email.isRequired}
										disabled={!patientConfig.email.isEditable}
										shouldValidate={shouldValidate}
										onChange={onPatientChange}
										isPacInValid={isEmailPacInValid}
									/>
								</Col>
							)}
						</Row>
					</div>
				</Col>
				{isPacValidated &&
					<Col xl="4" xxl="3" className="patient-details-from-patient-access-curator-area">
						<div>
							<PatientDetailsFromPatientAccessCurator
								formattedPacResponse={formattedPacResponse}
								handleUpdateInfo={handleUpdateInfo}
								isFirstNamePacInValid={isFirstNamePacInValid}
								isMiddleNamePacInValid={isMiddleNamePacInValid}
								isLastNamePacInValid={isLastNamePacInValid}
								isDateOfBirthPacInValid={isDateOfBirthPacInValid}
								isGenderPacInValid={isGenderPacInValid}
								isMobilePhonePacInValid={isMobilePhonePacInValid}
								isHomePhonePacInValid={isHomePhonePacInValid}
								isAlternatePhonePacInValid={isAlternatePhonePacInValid}
								isAddressLine1PacInValid={isAddressLine1PacInValid}
								isAddressLine2PacInValid={isAddressLine2PacInValid}
								isCityNamePacInValid={isCityNamePacInValid}
								isStateIdPacInValid={isStateIdPacInValid}
								isZipCodePacInValid={isZipCodePacInValid}
								isEmailPacInValid={isEmailPacInValid}
							/>
						</div>
					</Col>
				}
			</Row>
			<Row className="action-buttons">
				<ButtonToolbar>
					{(!isPacValidationForCreateEnabled || !isPatientAccessCuratorEnabled || isPacValidated) && (
						<SaveButton onClick={props.onSave} isBusy={props.isSaving} />
					)}
					{isPatientAccessCuratorEnabled && isPacValidationForCreateEnabled && (
						<PatientValidateButton onClick={handleValidate} isBusy={isPacValidating} />
					)}
					<CancelButton onClick={props.onCancel} />
				</ButtonToolbar>
			</Row>
		</Form>
	);
}

const AddressCheckbox = ({ onChange, isManualEntry, disabled }) => {
	return (
		<div className="address-search-checkbox">
			<label>Enter Address Manually?</label>
			<input
				id="address-checkbox"
				type="checkbox"
				onChange={e => onChange(e.target.checked)}
				value={isManualEntry}
				disabled={disabled}
			/>
		</div>
	)
}
